import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { useUserStore } from '@/piniaStores/user/user.store';
import { createSession, CreateSessionOptions } from '@/services/session';

export interface FetchInParallelChunksArgs {
  url: string;
  params: Record<string, unknown>;
  /** Adds the company slug to the URL */
  isCompanySpecific?: boolean;
  /**
   * The algorithm used to convert a query param whose value is an array into to a query string.
   * The default value is "repeat".
   */
  arrayFormat?: CreateSessionOptions['arrayFormat'];
  options?: { requestId?: string };
}

/**
 * Fetches the given request by splitting it up into parallel requests each with a limit of 500.
 * The purpose of a max limit of 500 is to avoid server errors caused by timeouts or server overload.
 *
 * If the given limit is 500 or less, only one request is made.
 *
 * Note: This function should not be considered a long-term solution. Instead, either of the following is a better long-term:
 *   1) all endpoints can handle any limit size without timing out or becoming overloaded
 *   2) all requests use a limit that is small enough to prevent the server timing out or becoming overloaded with a single request
 */
export async function fetchInParallelChunks<T extends object>({
  url,
  params,
  isCompanySpecific,
  arrayFormat,
  options,
}: FetchInParallelChunksArgs): Promise<PaginatedResponse<T>> {
  const userStore = useUserStore();

  const baseUrl = isCompanySpecific ? URLS.INTERNAL + userStore.userActiveCompany?.slug : URLS.INTERNAL;
  const client = createSession(baseUrl, { arrayFormat });

  async function fetchChunk({ limit, offset = 0 }: { limit: number; offset?: number }) {
    const response = await client.get<PaginatedResponse<T>>(url, {
      params: {
        ...params,
        offset,
        limit,
      },
      requestId: options?.requestId,
    });

    return response.data;
  }

  const paramsLimit = Number(params.limit) || 9999;
  const paramsOffset = Number(params.offset) || 0;

  if (paramsLimit <= 500) {
    return fetchChunk({ limit: paramsLimit, offset: paramsOffset });
  }

  const limit = paramsLimit > 500 ? 500 : paramsLimit;
  let offset = paramsOffset;
  let results: T[] = [];

  // Get the totalCount ASAP using a limit of 1
  const totalCount = await (async function () {
    const response = await client.get<PaginatedResponse<T>>(url, {
      params: { ...params, offset, limit: 1 },
      requestId: options?.requestId,
    });

    return response?.data.count || 0;
  })();

  const requests: ReturnType<typeof fetchChunk>[] = [];

  for (offset; offset < totalCount; offset += 500) {
    requests.push(fetchChunk({ offset, limit }));
  }

  (await Promise.all(requests)).forEach((chunk) => {
    results = results.concat(chunk.results);
  });

  return {
    count: totalCount,
    results,
  };
}
